'use client'

import {
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	Heading, Input,
	InputGroup,
	InputLeftElement,
	Textarea,
	VStack, useBreakpointValue,
	Alert,
	AlertIcon
} from '@chakra-ui/react'
import { BsPerson } from 'react-icons/bs'
import { MdOutlineEmail } from 'react-icons/md'
import Layout from '../components/layouts/main'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { motion } from 'framer-motion'
import { useState } from 'react'
import api from '../utils/axios'
import SocialLinks from '../components/SocialLinks'

const MotionHeading = motion(Heading);

const ContactPage = () => {
	const isMobile = useBreakpointValue({ base: true, md: false });
	const [showAlert, setShowAlert] = useState(false); // State for showing alert

	// Validation schema with Yup
	const validationSchema = Yup.object({
		name: Yup.string().required('Name is required'),
		email: Yup.string().email('Invalid email address').required('Email is required'),
		message: Yup.string().required('Message is required'),
	})

	// Form submit handler
	const handleSubmit = async (values, actions) => {
		try {
			// Send data to backend
			const response = await api.post('/contact', values)
			// Clear form after successful submission
			actions.resetForm()
			// Show alert
			setShowAlert(true);
			// Hide alert after 3 seconds
			setTimeout(() => {
				setShowAlert(false);
			}, 3000);
		} catch (error) {
			console.error('Error:', error)
		} finally {
			actions.setSubmitting(false)
		}
	}

	return (
		<Layout>
			<Box
				padding={isMobile ? 4 : 4}
				color="black"
				display="flex"
				justifyContent="center"
				margin={4}
			>
				<Box
					maxWidth="500px"
					width="100%"
				>
					<VStack spacing={6} align="center">
						<MotionHeading
							fontSize="clamp(1.5em, 2.8vw, 2.4em)"
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 1 }}
							textAlign={"center"}
						>
							Contact
						</MotionHeading>

						{showAlert && (
							<Alert status="success" borderRadius="md" variant="left-accent">
								<AlertIcon />
								Your message has been sent. We will get back to you soon.
							</Alert>
						)}

						<Box
							// bg={'#FEEFDD'}
							borderRadius="lg"
							p={6}
							width="100%"
						>
							<Formik
								initialValues={{ name: '', email: '', message: '' }}
								validationSchema={validationSchema}
								validateOnBlur={false}
								validateOnChange={false}
								onSubmit={handleSubmit}
							>
								{({ isSubmitting, errors, touched }) => (
									<Form>
										<VStack spacing={5} align="stretch"> {/* Ensure full width alignment */}
											<FormControl isInvalid={errors.name && touched.name} isRequired>
												<InputGroup>
													<InputLeftElement
														pointerEvents="none"
														width="3rem" // Ensure enough space for the icon
														display="flex"
														alignItems="center"
														justifyContent="center"
													>
														<BsPerson color="gray.500" size={20} /> {/* Adjust icon size */}
													</InputLeftElement>
													<Field
														as={Input}
														type="text"
														name="name"
														placeholder="Name"
														isFullWidth // Make input full width
														pl="3rem" // Make space for the icon
														border="1px solid rgb(255, 104, 75)"
														outline="1px solid rgb(255, 104, 75)"
														_focus={{
															borderColor: "rgb(255, 104, 75)",
															outline: "1px solid rgb(255, 104, 75)"
														}}
													/>
												</InputGroup>
												<FormErrorMessage>{errors.name}</FormErrorMessage>
											</FormControl>

											<FormControl isInvalid={errors.email && touched.email} isRequired>
												<InputGroup>
													<InputLeftElement
														pointerEvents="none"
														width="3rem" // Ensure enough space for the icon
														display="flex"
														alignItems="center"
														justifyContent="center"
													>
														<MdOutlineEmail color="gray.500" size={20} /> {/* Adjust icon size */}
													</InputLeftElement>
													<Field
														as={Input}
														type="email"
														name="email"
														placeholder="Email"
														isFullWidth // Make input full width
														pl="3rem" // Make space for the icon
														border="1px solid rgb(255, 104, 75)"
														outline="1px solid rgb(255, 104, 75)"
														_focus={{
															borderColor: "rgb(255, 104, 75)",
															outline: "1px solid rgb(255, 104, 75)"
														}}
													/>
												</InputGroup>
												<FormErrorMessage>{errors.email}</FormErrorMessage>
											</FormControl>

											<FormControl isInvalid={errors.message && touched.message} isRequired>
												<Field
													as={Textarea}
													name="message"
													placeholder="Message"
													rows={6}
													resize="none"
													isFullWidth // Make textarea full width
													border="1px solid rgb(255, 104, 75)"
													outline="1px solid rgb(255, 104, 75)"
													_focus={{
														borderColor: "rgb(255, 104, 75)",
														outline: "1px solid rgb(255, 104, 75)"
													}}
												/>
												<FormErrorMessage>{errors.message}</FormErrorMessage>
											</FormControl>

											<Button
												width="full" // Make button full width
												type="submit"
												isLoading={isSubmitting}
												background="#FF684B"
												color="white"
												_hover={{ background: "#FF5733" }}
											>
												Send Message
											</Button>
										</VStack>
									</Form>
								)}
							</Formik>
						</Box>

						<SocialLinks />
					</VStack>
				</Box>
			</Box>
		</Layout>
	)
}

export default ContactPage
